import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  FiArrowRight,
  FiCheck,
  FiPlay,
  FiTrendingUp,
  FiAlertCircle,
} from 'react-icons/fi';
import {
  moduleGroups,
  moduleCount,
  pillars,
  capabilities,
} from '../data/product';
import { Reveal, SectionHeading, AnimatedCounter } from '../components/ui';
import { PricingCards, BillingToggle, type BillingCycle } from './Pricing';

/* ------------------------------------------------------------------ *
 * Hero product mock
 * ------------------------------------------------------------------ */

/**
 * A stylised BuildFlow window, built entirely from divs. Deliberately not a
 * screenshot: it stays sharp at any resolution, weighs nothing, and can't go
 * stale when the real dashboard changes.
 */
const HeroMock: React.FC = () => {
  const bars = [42, 68, 55, 88, 72, 96, 61];
  const rows = [
    { label: 'Riverside Villa', meta: 'Kandivali · Ongoing', amount: '₹42,80,000', tone: 'text-emerald-400' },
    { label: 'Metro Plaza Fitout', meta: 'Andheri · On track', amount: '₹18,60,000', tone: 'text-emerald-400' },
    { label: 'Skyline Offices', meta: 'Powai · Over budget', amount: '₹31,05,000', tone: 'text-amber-400' },
  ];

  return (
    <div className="relative rounded-2xl border border-white/10 bg-ink-800/80 p-2.5 shadow-2xl backdrop-blur-sm">
      {/* Window chrome */}
      <div className="flex items-center gap-2 px-2 pb-2.5 pt-1">
        <span className="h-2.5 w-2.5 rounded-full bg-red-400/70" />
        <span className="h-2.5 w-2.5 rounded-full bg-amber-400/70" />
        <span className="h-2.5 w-2.5 rounded-full bg-emerald-400/70" />
        <div className="ml-3 flex-1 rounded-md bg-white/5 px-3 py-1 text-[10px] text-slate-400">
          app.buildflow.in / dashboard
        </div>
      </div>

      <div className="overflow-hidden rounded-xl bg-ink-900">
        <div className="flex">
          {/* Sidebar */}
          <div className="hidden w-36 flex-none border-r border-white/5 p-3 sm:block">
            <div className="mb-4 flex items-center gap-1.5">
              <div className="h-5 w-5 rounded-md bg-gradient-to-br from-primary-500 to-accent-500" />
              <span className="font-display text-[11px] font-bold text-white">BuildFlow</span>
            </div>
            {['Dashboard', 'Projects', 'Leads', 'Attendance', 'Vendors', 'Purchase Orders', 'Invoices', 'Reports'].map(
              (item, i) => (
                <div
                  key={item}
                  className={`mb-1 flex items-center gap-2 rounded-md px-2 py-1.5 text-[10px] ${
                    i === 0 ? 'bg-primary-600/25 text-white' : 'text-slate-400'
                  }`}
                >
                  <span
                    className={`h-1.5 w-1.5 rounded-full ${i === 0 ? 'bg-accent-400' : 'bg-slate-600'}`}
                  />
                  {item}
                </div>
              )
            )}
          </div>

          {/* Main panel */}
          <div className="min-w-0 flex-1 p-3.5">
            {/* Stat tiles */}
            <div className="mb-3 grid grid-cols-3 gap-2">
              {[
                { label: 'Revenue', value: '₹92.4L', delta: '+12%' },
                { label: 'Spend', value: '₹58.1L', delta: '+4%' },
                { label: 'Margin', value: '37%', delta: '+3%' },
              ].map((s) => (
                <div key={s.label} className="rounded-lg border border-white/5 bg-white/[0.03] p-2.5">
                  <div className="text-[9px] uppercase tracking-wide text-slate-500">{s.label}</div>
                  <div className="mt-0.5 font-display text-sm font-bold text-white">{s.value}</div>
                  <div className="flex items-center gap-0.5 text-[9px] text-emerald-400">
                    <FiTrendingUp className="h-2.5 w-2.5" />
                    {s.delta}
                  </div>
                </div>
              ))}
            </div>

            {/* Chart */}
            <div className="mb-3 rounded-lg border border-white/5 bg-white/[0.03] p-3">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-[10px] font-medium text-slate-300">Project spend vs budget</span>
                <span className="text-[9px] text-slate-500">Last 7 months</span>
              </div>
              <div className="flex h-16 items-end gap-1.5">
                {bars.map((h, i) => (
                  <motion.div
                    key={i}
                    initial={{ height: '8%' }}
                    animate={{ height: `${h}%` }}
                    transition={{ delay: 0.6 + i * 0.07, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                    className={`flex-1 rounded-sm ${
                      i === 5
                        ? 'bg-gradient-to-t from-accent-500 to-accent-300'
                        : 'bg-gradient-to-t from-primary-600 to-primary-400'
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* Rows */}
            <div className="rounded-lg border border-white/5 bg-white/[0.03]">
              {rows.map((r, i) => (
                <div
                  key={r.label}
                  className={`flex items-center justify-between px-3 py-2 ${
                    i !== rows.length - 1 ? 'border-b border-white/5' : ''
                  }`}
                >
                  <div className="min-w-0">
                    <div className="truncate text-[10px] font-medium text-white">{r.label}</div>
                    <div className="truncate text-[9px] text-slate-500">{r.meta}</div>
                  </div>
                  <div className={`flex-none text-[10px] font-semibold ${r.tone}`}>{r.amount}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Floating notification - the auto-raised vendor bill, which is a real behaviour */}
      <motion.div
        initial={{ opacity: 0, y: 12, scale: 0.94 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ delay: 1.5, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="absolute -bottom-5 -right-3 hidden max-w-[220px] rounded-xl border border-white/10 bg-ink-700/95 p-3 shadow-xl backdrop-blur-md sm:block"
      >
        <div className="flex items-start gap-2.5">
          <span className="mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-full bg-emerald-500/20 text-emerald-400">
            <FiCheck className="h-3.5 w-3.5" strokeWidth={3} />
          </span>
          <div>
            <div className="text-[11px] font-semibold text-white">PO-2041 received</div>
            <div className="mt-0.5 text-[10px] leading-snug text-slate-400">
              Vendor bill raised automatically · ₹3,24,000
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

/* ------------------------------------------------------------------ *
 * Hero
 * ------------------------------------------------------------------ */

const ROTATING = ['projects', 'site labour', 'purchase orders', 'vendor bills', 'attendance', 'cash flow'];

const Hero: React.FC = () => {
  const [word, setWord] = React.useState(0);

  React.useEffect(() => {
    const t = setInterval(() => setWord((w) => (w + 1) % ROTATING.length), 2200);
    return () => clearInterval(t);
  }, []);

  return (
    <section className="relative overflow-hidden bg-ink-900 pb-20 pt-28 sm:pt-36 lg:pb-28">
      {/* Blueprint grid + drifting glows */}
      <div className="absolute inset-0 blueprint-grid mask-fade opacity-80" aria-hidden />
      <div
        className="absolute -left-24 top-10 h-80 w-80 animate-blob rounded-full bg-primary-600/25 blur-3xl"
        aria-hidden
      />
      <div
        className="absolute -right-20 top-40 h-96 w-96 animate-blob-delayed rounded-full bg-accent-500/15 blur-3xl"
        aria-hidden
      />

      <div className="container-custom relative">
        <div className="grid items-center gap-14 lg:grid-cols-[1.05fr_1fr] lg:gap-12">
          {/* Copy */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <span className="pill-invert">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent-400 opacity-75" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-accent-400" />
                </span>
                {moduleCount} modules · now live
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.08 }}
              className="mt-6 font-display text-4xl font-bold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl"
            >
              Run your whole
              <br />
              practice from
              <span className="text-gradient-animate"> one place</span>
            </motion.h1>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mt-5 flex h-7 items-center gap-2 text-base text-slate-400 sm:text-lg"
            >
              <span>No more spreadsheets for</span>
              <AnimatePresence mode="wait">
                <motion.span
                  key={word}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.35 }}
                  className="font-semibold text-accent-300"
                >
                  {ROTATING[word]}
                </motion.span>
              </AnimatePresence>
            </motion.div>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.28 }}
              className="mt-6 max-w-xl lede-invert"
            >
              BuildFlow is the ERP built for architecture and construction firms — {moduleCount}{' '}
              connected modules covering sales, delivery, workforce, procurement and finance. Every
              rupee you commit shows up against the right project, automatically.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.36 }}
              className="mt-9 flex flex-col gap-3 sm:flex-row"
            >
              <Link to="/contact" className="btn-primary">
                Start free
                <FiArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/pricing" className="btn-ghost-invert">
                <FiPlay className="h-4 w-4" />
                See pricing
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.46 }}
              className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-slate-400"
            >
              {['From ₹999/month', 'No card required', 'Your data stays yours'].map((t) => (
                <span key={t} className="flex items-center gap-1.5">
                  <FiCheck className="h-3.5 w-3.5 text-emerald-400" strokeWidth={3} />
                  {t}
                </span>
              ))}
            </motion.div>
          </div>

          {/* Product mock */}
          <motion.div
            initial={{ opacity: 0, y: 36, rotateX: 8 }}
            animate={{ opacity: 1, y: 0, rotateX: 0 }}
            transition={{ duration: 0.9, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="relative lg:pl-4"
          >
            <HeroMock />
          </motion.div>
        </div>
      </div>

      {/* Hairline into the next section */}
      <div className="absolute bottom-0 left-0 right-0 h-px hairline" aria-hidden />
    </section>
  );
};

/* ------------------------------------------------------------------ *
 * The problem we solve
 * ------------------------------------------------------------------ */

const PainPoints: React.FC = () => {
  const pains = [
    'Site expenses land in WhatsApp, not in your accounts',
    'Nobody can say what a project actually cost until it is finished',
    'Purchase orders live in one file, supplier bills in another',
    'Attendance is on paper, so payroll takes three days',
  ];

  return (
    <section className="relative bg-ink-800 py-16 sm:py-20">
      <div className="container-custom">
        <div className="grid gap-10 lg:grid-cols-2 lg:gap-16">
          <div>
            <Reveal>
              <span className="eyebrow mb-4 text-amber-400">
                <FiAlertCircle className="h-3.5 w-3.5" />
                Sound familiar?
              </span>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="h-section-invert">
                Most firms don't lose money on site. They lose it in the gaps.
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-4 lede-invert">
                Every disconnected tool is a place where a cost goes missing. BuildFlow closes the
                gaps by making one module's output another module's input.
              </p>
            </Reveal>
          </div>

          <div className="space-y-3">
            {pains.map((p, i) => (
              <Reveal key={p} delay={i * 0.07}>
                <div className="glass flex items-start gap-3 p-4">
                  <span className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-amber-500/15 text-amber-400">
                    <span className="text-xs font-bold">!</span>
                  </span>
                  <p className="text-sm text-slate-300">{p}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ *
 * Module showcase - the new scope
 * ------------------------------------------------------------------ */

const Modules: React.FC = () => (
  <section id="platform" className="section relative scroll-mt-24 bg-white">
    <div className="absolute inset-x-0 top-0 h-64 blueprint-grid-fine mask-fade-b opacity-60" aria-hidden />
    <div className="container-custom relative">
      <SectionHeading
        eyebrow="The platform"
        title={
          <>
            {moduleCount} modules. <span className="text-gradient">One system.</span>
          </>
        }
        sub="Not a bundle of separate apps. Every module reads and writes the same records, so a purchase order, the stock it delivers and the bill you owe are all the same story."
      />

      <div className="mt-14 space-y-10">
        {moduleGroups.map((group, gi) => (
          <Reveal key={group.group} delay={gi * 0.05}>
            <div>
              {/* Group header */}
              <div className="mb-5 flex items-center gap-4">
                <div className="flex items-center gap-3">
                  <span className={`h-8 w-1 rounded-full bg-gradient-to-b ${group.gradient}`} />
                  <div>
                    <h3 className="font-display text-lg font-bold text-ink-800">{group.group}</h3>
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
                      {group.tag}
                    </p>
                  </div>
                </div>
                <div className="h-px flex-1 bg-slate-200" />
                <span className="flex-none rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-500">
                  {group.items.length}
                </span>
              </div>

              {/* Module cards */}
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {group.items.map((m, i) => (
                  <Reveal key={m.name} delay={i * 0.04}>
                    <div className="card-interactive group h-full p-5">
                      <div
                        className={`mb-3.5 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br ${group.gradient} text-white shadow-sm transition-transform duration-300 group-hover:scale-110`}
                      >
                        <m.icon className="h-5 w-5" aria-hidden />
                      </div>
                      <h4 className="font-semibold text-ink-800">{m.name}</h4>
                      <p className="mt-1.5 text-sm leading-relaxed text-slate-500">{m.desc}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ------------------------------------------------------------------ *
 * Pillars - why it is an ERP, not a CRUD app
 * ------------------------------------------------------------------ */

const Pillars: React.FC = () => (
  <section className="section relative overflow-hidden bg-ink-900">
    <div className="absolute inset-0 blueprint-grid opacity-40" aria-hidden />
    <div
      className="absolute right-0 top-1/3 h-96 w-96 animate-blob rounded-full bg-primary-700/20 blur-3xl"
      aria-hidden
    />
    <div className="container-custom relative">
      <SectionHeading
        eyebrow="Built like an ERP"
        title={
          <>
            The parts you can't <span className="text-gradient">bolt on later</span>
          </>
        }
        sub="Any tool can store a list. These three are what make the numbers trustworthy — and they only work if they were designed in from the start."
        invert
      />

      <div className="mt-14 space-y-6">
        {pillars.map((p, i) => (
          <Reveal key={p.title} delay={i * 0.08}>
            <div className="glass grid gap-6 p-6 sm:p-8 lg:grid-cols-[auto_1fr_1fr] lg:items-start lg:gap-10">
              <div className="flex items-center gap-4 lg:flex-col lg:items-start">
                <span className="flex h-12 w-12 flex-none items-center justify-center rounded-2xl bg-gradient-to-br from-primary-500 to-accent-500 text-white shadow-glow">
                  <p.icon className="h-6 w-6" aria-hidden />
                </span>
                <span className="font-display text-xs font-bold uppercase tracking-[0.16em] text-accent-300">
                  {p.kicker}
                </span>
              </div>

              <div>
                <h3 className="font-display text-xl font-bold text-white sm:text-2xl">{p.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-300 sm:text-base">{p.desc}</p>
              </div>

              <ul className="space-y-2.5 lg:border-l lg:border-white/10 lg:pl-8">
                {p.points.map((pt) => (
                  <li key={pt} className="flex items-start gap-2.5 text-sm text-slate-300">
                    <FiCheck className="mt-0.5 h-4 w-4 flex-none text-accent-400" strokeWidth={3} />
                    {pt}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        ))}
      </div>

      {/* Capability strip */}
      <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {capabilities.map((c, i) => (
          <Reveal key={c.label} delay={i * 0.06}>
            <div className="h-full rounded-2xl border border-white/10 bg-white/[0.03] p-5">
              <c.icon className="mb-3 h-5 w-5 text-accent-400" aria-hidden />
              <h4 className="text-sm font-semibold text-white">{c.label}</h4>
              <p className="mt-1.5 text-xs leading-relaxed text-slate-400">{c.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ------------------------------------------------------------------ *
 * Stats
 * ------------------------------------------------------------------ */

const Stats: React.FC = () => {
  const stats = [
    { value: moduleCount, suffix: '', label: 'Connected modules' },
    { value: 15, suffix: '+', label: 'Built-in reports' },
    { value: 5, suffix: 'x', label: 'Faster month-end close' },
    { value: 99, suffix: '.9%', label: 'Uptime target' },
  ];

  return (
    <section className="border-y border-slate-200 bg-slate-50 py-14">
      <div className="container-custom">
        <div className="grid grid-cols-2 gap-8 text-center lg:grid-cols-4">
          {stats.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.06}>
              <div>
                <div className="font-display text-3xl font-bold text-ink-800 sm:text-4xl">
                  <span className="text-gradient">
                    <AnimatedCounter value={s.value} suffix={s.suffix} />
                  </span>
                </div>
                <div className="mt-1.5 text-sm text-slate-500">{s.label}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ *
 * Pricing teaser
 * ------------------------------------------------------------------ */

const PricingTeaser: React.FC = () => {
  const [cycle, setCycle] = React.useState<BillingCycle>('monthly');

  return (
    <section id="pricing" className="section scroll-mt-24 bg-white">
      <div className="container-custom">
        <SectionHeading
          eyebrow="Pricing"
          title={
            <>
              Priced for real firms, <span className="text-gradient">not enterprises</span>
            </>
          }
          sub="Every plan unlocks whole modules — no per-seat traps, no add-on fees. Start where you are and move up when you grow."
        />

        <div className="mt-9 flex justify-center">
          <BillingToggle cycle={cycle} onChange={setCycle} />
        </div>

        <div className="mt-12">
          <PricingCards cycle={cycle} />
        </div>

        <Reveal>
          <div className="mt-10 text-center">
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 text-sm font-semibold text-primary-600 transition-colors hover:text-primary-700"
            >
              Compare plans in detail
              <FiArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ *
 * Closing CTA
 * ------------------------------------------------------------------ */

const FinalCTA: React.FC = () => (
  <section className="section bg-slate-50">
    <div className="container-custom">
      <div className="relative overflow-hidden rounded-3xl bg-ink-900 px-6 py-16 sm:px-14 sm:py-20">
        <div className="absolute inset-0 blueprint-grid opacity-50" aria-hidden />
        <div
          className="absolute -left-16 bottom-0 h-72 w-72 animate-blob rounded-full bg-primary-600/25 blur-3xl"
          aria-hidden
        />
        <div
          className="absolute -right-10 -top-10 h-72 w-72 animate-blob-delayed rounded-full bg-accent-500/20 blur-3xl"
          aria-hidden
        />

        <div className="relative mx-auto max-w-2xl text-center">
          <Reveal>
            <span className="pill-invert mb-6">
              <span className="h-1.5 w-1.5 rounded-full bg-accent-400" />
              See it with your own projects
            </span>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display text-3xl font-bold leading-tight text-white sm:text-4xl md:text-5xl">
              Your firm already has the data.
              <span className="text-gradient-animate"> Let's put it to work.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mx-auto mt-5 max-w-xl lede-invert">
              Book a 30-minute walkthrough. We'll load a couple of your live projects into a private
              workspace so you can see exactly what BuildFlow would tell you about them.
            </p>
          </Reveal>
          <Reveal delay={0.16}>
            <div className="mt-9 flex flex-col justify-center gap-3 sm:flex-row">
              <Link to="/contact" className="btn-primary">
                Book a free demo
                <FiArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/pricing" className="btn-ghost-invert">
                View pricing
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.22}>
            <p className="mt-6 text-xs text-slate-500">
              No commitment · No card required · Setup in under a day
            </p>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);

/* ------------------------------------------------------------------ */

export const Home: React.FC = () => (
  <div className="overflow-hidden">
    <Hero />
    <PainPoints />
    <Modules />
    <Pillars />
    <Stats />
    <PricingTeaser />
    <FinalCTA />
  </div>
);

export default Home;
